/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import type { EuiBasicTableColumn } from '@elastic/eui'
import { EuiBasicTable, EuiFlexGroup, EuiFlexItem, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type {
  RoleAssignmentsTableItem,
  TableItemRoleDescriptor,
} from '@modules/role-assignments-lib/types'
import { resourceRoleLabelByRoleId } from '@modules/role-assignments-lib/types'
import { ccmClusterRoleLabels } from '@modules/role-assignments-lib/messages'

import type { MessageDescriptor } from 'react-intl'
import type { FunctionComponent } from 'react'

const CCMRoleAssignmentsTable: FunctionComponent<{
  items: Array<RoleAssignmentsTableItem<'cloud-connected'>>
}> = ({ items }) => (
  <EuiBasicTable
    data-test-id='cloud-connected-role-assignments-table'
    noItemsMessage={
      <FormattedMessage
        id='roles-assignments.no-cloud-connected-found'
        defaultMessage='No connected clusters found'
      />
    }
    items={items}
    columns={getColumns()}
  />
)

const getColumns = (): Array<EuiBasicTableColumn<RoleAssignmentsTableItem<'cloud-connected'>>> => [
  {
    name: <FormattedMessage id='cloud-connected.roles-assignments.name' defaultMessage='Name' />,
    field: 'name',
    sortable: ({ name }) => name.toLowerCase(),
    render: (name: string) => <EuiText size='s'>{name}</EuiText>,
    truncateText: true,
  },
  {
    name: (
      <FormattedMessage id='cloud-connected.roles-assignments.roleColumn' defaultMessage='Role' />
    ),
    field: 'roles',
    render: (
      roles: TableItemRoleDescriptor[],
      roleAssignmentItem: RoleAssignmentsTableItem<'cloud-connected'>,
    ) => {
      const roleItems = roles.map((roleDescriptor) => {
        const roleLabel = resourceRoleLabelByRoleId[roleDescriptor.roleId]
        const predefinedLabels: Record<string, MessageDescriptor> = ccmClusterRoleLabels
        return (
          <EuiFlexItem grow={false} key={roleAssignmentItem.id.concat(roleDescriptor.roleId)}>
            <FormattedMessage {...predefinedLabels[roleLabel]} />
          </EuiFlexItem>
        )
      })

      return (
        <EuiFlexGroup direction='column' justifyContent='flexStart' gutterSize='xs'>
          {roleItems}
        </EuiFlexGroup>
      )
    },
  },
]

export default CCMRoleAssignmentsTable
